import React, { useState } from "react";
import { Input, Button, Form, Col, Row, Dropdown, DropdownItem, DropdownMenu, DropdownToggle } from "reactstrap";
import EarningsTable from "./EarningsTable";
import { MONTH } from "helpers/fn_helper";

// Custom Hooks
import useTableController from "hooks/useTableController";

// Validation
import { YearMonthSchema } from "model/_base/filters/filter-options.schema";

// API
import { useGetEarningsQuery, useLazyExportEarningsQuery } from "services/transactions/api";

// Icons
import { ReactComponent as XlsIcon } from "assets/icons/xls.svg";

// Utils
import { downloadFile, generateTimestampedFilename } from "utils/fileUtils";

const EarningsTab = () => {
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [monthDropdownOpen, setMonthDropdownOpen] = useState(false);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() - 1; // 0-11

  const toggleYearDropdown = () => setYearDropdownOpen(!yearDropdownOpen);
  const toggleMonthDropdown = () => setMonthDropdownOpen(!monthDropdownOpen);

  // HOOK CHANGE
  const { formik, searchInput, setSearchInput, handleTableChange, handleFieldChange } = useTableController({
    initialValues: {
      year: currentYear.toString(),
      month: MONTH[currentMonth].index,
    },
    validationSchema: YearMonthSchema,
  });

  // API
  const { data, error, isLoading } = useGetEarningsQuery(formik.values);
  const [exports] = useLazyExportEarningsQuery();

  // ACTIONS
  const onExport = async () => {
    try {
      const response = await exports(formik.values);
      const fileName = generateTimestampedFilename("transections-earnings");
      await downloadFile(response.data, fileName);
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  // RENDER
  return (
    <Form onSubmit={formik.handleSubmit}>
      <div className="transections-container" data-testid="tab-transactions">
        <div className="header mt-4">
          <div className="d-flex flex-column gap-2">
            <Row className="gy-3">
              <Col sm={3} md={2} lg={2}>
                <Dropdown isOpen={yearDropdownOpen} toggle={toggleYearDropdown}>
                  <DropdownToggle caret className="btn btn-outline-secondary">
                    {formik.values.year}
                  </DropdownToggle>
                  <DropdownMenu>
                    {Array.from(
                      { length: new Date().getFullYear() - 2026 + 1 },
                      (_, i) => 2026 + i
                    ).reverse().map((year, index) => (
                      <DropdownItem
                        key={index}
                        onClick={() => handleFieldChange("year", year.toString())}
                        className={formik.values.year === year.toString() ? "active" : ""}
                      >
                        {year}
                      </DropdownItem>
                    ))}
                  </DropdownMenu>
                </Dropdown>
              </Col>
              <Col sm={3} md={3} lg={2}>
                <Dropdown isOpen={monthDropdownOpen} toggle={toggleMonthDropdown}>
                  <DropdownToggle caret className="btn btn-outline-secondary">
                    {MONTH.find((m) => m.index === parseInt(formik.values.month))?.label || "เลือกเดือน"}
                  </DropdownToggle>
                  <DropdownMenu>
                    {MONTH.map((month, index) => {
                      const currentDate = new Date();
                      const currentYear = currentDate.getFullYear();
                      const currentMonth = currentDate.getMonth() + 1; // Months are 0-indexed in JS
                      const isCurrentYear = parseInt(formik.values.year) === currentYear;
                      const isFutureMonth = isCurrentYear && month.index > currentMonth - 1; // -1 to disable current month
                      const isPastMonth = isCurrentYear && month.index < currentMonth;

                      // If it's the current year, disable current and future months
                      // If it's a past year, all months are enabled
                      const isDisabled = false;// isCurrentYear ? month.index >= currentMonth : false;

                      return (
                        <DropdownItem
                          key={index}
                          onClick={() => !isDisabled && handleFieldChange("month", month.index.toString())}
                          className={`
                            ${formik.values.month && parseInt(formik.values.month) === month.index ? "active" : ""}
                            ${isDisabled ? "!text-gray-300" : ""}
                          `}
                          disabled={isDisabled}
                        >
                          {month.label}
                        </DropdownItem>
                      );
                    })}
                  </DropdownMenu>
                </Dropdown>
              </Col>
              <Col sm={6} md={{ offset: 2, size: 5 }} lg={{ offset: 3, size: 5 }}>
                <div className="d-flex gap-3">
                  <div className="flex-grow-1">
                    <div className="input-group-icon">
                      <i className="bx bx-search" />
                      <Input name="searchTerm" type="text" placeholder="ค้นหารายการพาร์ทเนอร์" onChange={(e) => setSearchInput(e.target.value)} value={searchInput} data-testid="search-input" invalid={formik.touched.searchTerm && formik.errors.searchTerm} />
                      {formik.touched.searchTerm && formik.errors.searchTerm && (
                        <div className="error-message" data-testid="search-error">
                          {formik.errors.searchTerm}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </Col>
            </Row>
          </div>
          <div className="text-xl mb-4 text-danger-600 mt-1">***ตัวกรองในระบบจะเป็นรายเดือน เพราะค่าคอมมิชชันจะถูกตัดรอบทุกวันที่ 30-31 ของเดือน</div>
          <div className="mt-4 d-flex justify-content-between">
            <h2 className="fw-semibold">
              รายชื่อพาร์ทเนอร์ที่ทำธุรกรรมในระบบประจำเดือน
              {MONTH.find((m) => m.index === formik.values.month)?.label || "เลือกเดือน"} {formik.values.year}
            </h2>
            <div>
              <Button color="success" size="xs" onClick={onExport} className="d-flex gap-2" data-testid="export-button">
                <XlsIcon />
                EXPORT EXCEL
              </Button>
            </div>
          </div>
        </div>
        <EarningsTable data={data} error={error} isLoading={isLoading} onTableChange={handleTableChange} />
      </div>
    </Form>
  );
};

export default EarningsTab;
